import { useMemo } from 'react';
import { IconLock } from '@rossum/ui/icons/tabler';
import {
  Chip,
  ChipProps,
  CircularProgress,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { useIntl } from '../ComponentsContext';

type DisplayedStatus =
  | 'created'
  | 'exporting'
  | 'deleted'
  | 'confirmed'
  | 'exported'
  | 'failedExport'
  | 'failedImport'
  | 'reviewing'
  | 'toReview'
  | 'importing'
  | 'split'
  | 'rejected'
  | 'postponed'
  | 'inWorkflow'
  | 'notExtracted';

const warningStatuses: DisplayedStatus[] = ['postponed'];
const successStatuses: DisplayedStatus[] = ['confirmed'];
const infoStatuses: DisplayedStatus[] = ['toReview'];
const errorStatuses: DisplayedStatus[] = [
  'failedExport',
  'failedImport',
  'rejected',
  'deleted',
  'notExtracted',
];

const getChipColor = (status: DisplayedStatus): ChipProps['color'] => {
  if (errorStatuses.includes(status)) {
    return 'error';
  }
  if (warningStatuses.includes(status)) {
    return 'warning';
  }
  if (successStatuses.includes(status)) {
    return 'success';
  }
  if (infoStatuses.includes(status)) {
    return 'info';
  }
  return 'default';
};

export type StatusChipProps = {
  status: DisplayedStatus;
  isOwned?: boolean;
  tooltipTitle?: string | null;
  dataCy?: string;
  disabled?: boolean;
};

const StatusChip = ({
  tooltipTitle,
  status,
  isOwned,
  disabled,
  dataCy,
}: StatusChipProps) => {
  const intl = useIntl();

  const Icon = useMemo(() => {
    if (status === 'reviewing' && !isOwned) {
      return (
        <SvgIcon color="secondary" fontSize="small">
          <IconLock />
        </SvgIcon>
      );
    }

    if (status === 'importing' || status === 'exporting') {
      return (
        <Stack pr={0.25} alignItems="center">
          <CircularProgress size={14} color="secondary" />
        </Stack>
      );
    }
    return undefined;
  }, [isOwned, status]);

  return (
    <Tooltip placement="top" title={tooltipTitle}>
      <Chip
        data-cy={dataCy}
        color={getChipColor(status)}
        disabled={disabled}
        sx={{
          cursor: 'default',
        }}
        icon={Icon}
        label={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography
              variant="caption"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {intl.formatMessage({
                id: `containers.annotationList.statuses.${status}`,
              })}
            </Typography>
          </Stack>
        }
      />
    </Tooltip>
  );
};

export default StatusChip;
